import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import {
  Settings, Save, RotateCcw, Loader2, MapPin, Plus, Trash2, Info,
  AlertCircle, PieChart, Calculator, Coins, X, Building2, Wrench
} from 'lucide-react';
import { Switch } from '../ui/switch';
import LiveSimulator from './LiveSimulator';
import { DEFAULT_CONFIG_FORM } from './hooks/useAdminConfig';

const OPERATION_LABELS = {
  construction_neuve: 'Construction neuve',
  extension: 'Extension',
  renovation_lourde: 'Rénovation lourde',
  rehabilitation: 'Réhabilitation',
  surelevation: 'Surélévation',
};

const BATIMENT_LABELS = {
  individuel: 'Individuel',
  collectif: 'Collectif',
  mixte: 'Mixte',
  tertiaire: 'Tertiaire',
};

export default function ConfigPanel({
  showConfigPanel, setShowConfigPanel,
  configForm, setConfigForm,
  savingConfig,
  saveAdminConfig,
  resetAdminConfig,
  adminConfig,
}) {
  if (!showConfigPanel) return null;

  const totalAffectation = ((configForm.affectation.fonds_garantie +
    configForm.affectation.prevention_expertise +
    configForm.affectation.gestion_solidaire) * 100);
  const _isValid = Math.abs(totalAffectation - 100) < 1;

  return (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-xl">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg text-blue-900">Configuration des taux DOMIUM</CardTitle>
                  <CardDescription className="text-blue-700">
                    Personnalisation des taux de contribution et d'affectation (SUPER ADMIN)
                  </CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowConfigPanel(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Taux de contribution */}
            <div className="space-y-4">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Taux applicable pour le calcul de la contribution
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Taux de base (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5"
                    value={(configForm.taux_base * 100).toFixed(1)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      taux_base: parseFloat(e.target.value) / 100
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 1.2%</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Taux minimum (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5"
                    value={(configForm.taux_min * 100).toFixed(1)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      taux_min: parseFloat(e.target.value) / 100
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 0.8%</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Taux maximum (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={(configForm.taux_max * 100).toFixed(1)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      taux_max: parseFloat(e.target.value) / 100
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 1.8%</p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Tranches de taux par seuil de montant */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Coins className="h-4 w-4" />
                  Tranches de taux par seuil de montant
                </h4>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-blue-700">Activer les tranches</Label>
                  <Switch
                    checked={configForm.tranches_taux_actives}
                    onCheckedChange={(checked) => setConfigForm({
                      ...configForm,
                      tranches_taux_actives: checked
                    })}
                  />
                </div>
              </div>
              
              {configForm.tranches_taux_actives && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Le taux de base est remplacé par le taux de la tranche correspondant au montant de l'opération.
                  </p>
                  
                  {configForm.tranches_taux.map((tranche, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
                      <div className="flex-1 grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs text-blue-600">Seuil min (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={tranche.seuil_min}
                            onChange={(e) => {
                              const newTranches = [...configForm.tranches_taux];
                              newTranches[index].seuil_min = parseInt(e.target.value) || 0;
                              setConfigForm({ ...configForm, tranches_taux: newTranches });
                            }}
                            className="border-blue-200 h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-600">Seuil max (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Illimité"
                            value={tranche.seuil_max || ''}
                            onChange={(e) => {
                              const newTranches = [...configForm.tranches_taux];
                              newTranches[index].seuil_max = e.target.value ? parseInt(e.target.value) : null;
                              setConfigForm({ ...configForm, tranches_taux: newTranches });
                            }}
                            className="border-blue-200 h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-600">Taux (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            max="10"
                            value={(tranche.taux * 100).toFixed(1)}
                            onChange={(e) => {
                              const newTranches = [...configForm.tranches_taux];
                              newTranches[index].taux = parseFloat(e.target.value) / 100;
                              setConfigForm({ ...configForm, tranches_taux: newTranches });
                            }}
                            className="border-blue-200 h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-600">Libellé</Label>
                          <Input
                            type="text"
                            placeholder="Ex: Petits projets"
                            value={tranche.label}
                            onChange={(e) => {
                              const newTranches = [...configForm.tranches_taux];
                              newTranches[index].label = e.target.value;
                              setConfigForm({ ...configForm, tranches_taux: newTranches });
                            }}
                            className="border-blue-200 h-9"
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newTranches = configForm.tranches_taux.filter((_, i) => i !== index);
                          setConfigForm({ ...configForm, tranches_taux: newTranches });
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        disabled={configForm.tranches_taux.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lastTranche = configForm.tranches_taux[configForm.tranches_taux.length - 1];
                      const newSeuilMin = lastTranche.seuil_max ? lastTranche.seuil_max + 1 : 0;
                      setConfigForm({
                        ...configForm,
                        tranches_taux: [
                          ...configForm.tranches_taux,
                          { seuil_min: newSeuilMin, seuil_max: null, taux: 0.010, label: '' }
                        ]
                      });
                    }}
                    className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une tranche
                  </Button>
                </div>
              )}
              
              {!configForm.tranches_taux_actives && (
                <div className="p-3 bg-gray-50 rounded-lg text-gray-600 text-sm">
                  <Info className="h-4 w-4 inline mr-2" />
                  Lorsque les tranches sont désactivées, le taux de base unique ({(configForm.taux_base * 100).toFixed(1)}%) est utilisé pour tous les montants.
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Coefficients par territoire */}
            <div className="space-y-4">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Coefficients par territoire
              </h4>
              <p className="text-sm text-gray-600">
                Ces coefficients ajustent le taux de base selon le territoire (risque sismique/cyclonique).
                Formule : Taux effectif = Taux base × Coefficient territoire
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { key: 'guadeloupe', label: 'Guadeloupe', default: 1.15 },
                  { key: 'martinique', label: 'Martinique', default: 1.15 },
                  { key: 'guyane', label: 'Guyane', default: 1.20 },
                  { key: 'reunion', label: 'La Réunion', default: 1.10 },
                  { key: 'mayotte', label: 'Mayotte', default: 1.25 },
                  { key: 'saint_martin', label: 'Saint-Martin', default: 1.20 },
                  { key: 'saint_barthelemy', label: 'Saint-Barthélemy', default: 1.15 },
                ].map((territoire) => (
                  <div key={territoire.key} className="space-y-1">
                    <Label className="text-xs text-blue-700">{territoire.label}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.5"
                      max="2.0"
                      value={(configForm.coeff_territoire?.[territoire.key] || territoire.default).toFixed(2)}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        coeff_territoire: {
                          ...configForm.coeff_territoire,
                          [territoire.key]: parseFloat(e.target.value) || territoire.default
                        }
                      })}
                      className="border-blue-200 h-9"
                    />
                    <p className="text-xs text-gray-400">Défaut: {territoire.default.toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                Un coefficient de 1.15 signifie +15% sur le taux de base. Plage autorisée : 0.50 à 2.00
              </div>
            </div>
            
            <Separator />
            
            {/* Affectation de la contribution */}
            <div className="space-y-4">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Taux d'affectation de la contribution
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Fonds de garantie (%)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={(configForm.affectation.fonds_garantie * 100).toFixed(0)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      affectation: {
                        ...configForm.affectation,
                        fonds_garantie: parseFloat(e.target.value) / 100
                      }
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 60%</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Prévention & Expertise (%)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={(configForm.affectation.prevention_expertise * 100).toFixed(0)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      affectation: {
                        ...configForm.affectation,
                        prevention_expertise: parseFloat(e.target.value) / 100
                      }
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 25%</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-blue-700">Gestion solidaire (%)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={(configForm.affectation.gestion_solidaire * 100).toFixed(0)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      affectation: {
                        ...configForm.affectation,
                        gestion_solidaire: parseFloat(e.target.value) / 100
                      }
                    })}
                    className="border-blue-200"
                  />
                  <p className="text-xs text-gray-500">Défaut: 15%</p>
                </div>
              </div>
              {/* Indicateur du total */}
              {(() => {
                const total = (configForm.affectation.fonds_garantie + 
                             configForm.affectation.prevention_expertise + 
                             configForm.affectation.gestion_solidaire) * 100;
                const isValid = Math.abs(total - 100) < 1;
                return (
                  <div className={`p-3 rounded-lg ${isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    <span className="font-medium">Total: {total.toFixed(0)}%</span>
                    {!isValid && <span className="ml-2">(doit être égal à 100%)</span>}
                  </div>
                );
              })()}
            </div>
            
            {/* V37.31 — Coefficients par type d'opération */}
            <div className="space-y-3" data-testid="config-coeff-operation-section">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Coefficients par type d&apos;opération
              </h4>
              <p className="text-[11px] text-blue-700/80">
                Multiplicateur applique au taux selon le type d&apos;operation immobiliere. Plage acceptee : 0.5 - 3.0
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(OPERATION_LABELS).map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-blue-700">{label}</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.5"
                      max="3.0"
                      value={configForm.coeff_operation?.[key] ?? 1.0}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        coeff_operation: { ...configForm.coeff_operation, [key]: parseFloat(e.target.value) || 0 },
                      })}
                      data-testid={`coeff-op-${key}`}
                      className="bg-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* V37.31 — Coefficients par type de bâtiment */}
            <div className="space-y-3" data-testid="config-coeff-batiment-section">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Coefficients par type de bâtiment
              </h4>
              <p className="text-[11px] text-blue-700/80">
                Multiplicateur applique au taux selon le type de batiment. Plage acceptee : 0.5 - 3.0
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(BATIMENT_LABELS).map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-blue-700">{label}</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.5"
                      max="3.0"
                      value={configForm.coeff_batiment?.[key] ?? 1.0}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        coeff_batiment: { ...configForm.coeff_batiment, [key]: parseFloat(e.target.value) || 0 },
                      })}
                      data-testid={`coeff-bat-${key}`}
                      className="bg-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* V37.31 — Réductions */}
            <div className="space-y-3" data-testid="config-reductions-section">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Réductions applicables
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">Mesures de prévention (%)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="50"
                    value={((configForm.reduction_prevention ?? 0) * 100).toFixed(2)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      reduction_prevention: (parseFloat(e.target.value) || 0) / 100,
                    })}
                    data-testid="reduction-prevention"
                    className="bg-white text-sm"
                  />
                  <p className="text-[10px] text-blue-600/70">Reduction appliquee si mesures de prevention activees.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">Entreprises ESS — 100% (%)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="50"
                    value={((configForm.reduction_ess ?? 0) * 100).toFixed(2)}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      reduction_ess: (parseFloat(e.target.value) || 0) / 100,
                    })}
                    data-testid="reduction-ess"
                    className="bg-white text-sm"
                  />
                  <p className="text-[10px] text-blue-600/70">Reduction proportionnelle a la part d&apos;entreprises ESS.</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* V37.32 — Simulateur live AVANT / APRES */}
            <LiveSimulator
              configBefore={adminConfig?.current_config && Object.keys(adminConfig.current_config).length > 0
                ? { ...DEFAULT_CONFIG_FORM, ...adminConfig.default_config, ...adminConfig.current_config }
                : (adminConfig?.default_config || DEFAULT_CONFIG_FORM)}
              configAfter={configForm}
            />

            {/* Boutons d'action */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={resetAdminConfig}
                disabled={savingConfig}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Réinitialiser
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfigPanel(false)}
                  disabled={savingConfig}
                >
                  Annuler
                </Button>
                <Button
                  onClick={saveAdminConfig}
                  disabled={savingConfig || Math.abs(
                    (configForm.affectation.fonds_garantie + 
                     configForm.affectation.prevention_expertise + 
                     configForm.affectation.gestion_solidaire) * 100 - 100) >= 1}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {savingConfig ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Info dernière modification */}
            {adminConfig?.current_config?.updated_at && (
              <div className="text-xs text-gray-500 text-right">
                Dernière modification: {new Date(adminConfig.current_config.updated_at).toLocaleString('fr-FR')} 
                par {adminConfig.current_config.updated_by}
              </div>
            )}
          </CardContent>
        </Card>
  );
}
